import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
  PhoneIcon,
  EnvelopeIcon,
  UserGroupIcon,
  TrophyIcon,
  ChartBarIcon,
  CalendarIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  FireIcon,
  BoltIcon
} from '@heroicons/react/24/outline';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';

const SalesDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('month');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, leaderboardRes] = await Promise.all([
        api.get('/api/sales/sales-stats'),
        api.get('/api/sales/leaderboard')
      ]);
      setStats(statsRes.data);
      setLeaderboard(leaderboardRes.data);
    } catch (error) {
      console.error('Failed to fetch sales data:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric'
    });
  };

  const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444'];

  if (loading) {
    return (
      <div className="dashboard">
        <div style={{ padding: 60, textAlign: 'center', color: '#718096' }}>
          <div style={{ 
            width: 48, 
            height: 48, 
            border: '3px solid #e2e8f0',
            borderTopColor: '#3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          Loading your dashboard...
        </div>
      </div>
    );
  }

  const pipelineData = stats?.leads ? [
    { name: 'New', value: parseInt(stats.leads.new) || 0, color: '#3b82f6' },
    { name: 'Contacted', value: parseInt(stats.leads.contacted) || 0, color: '#f59e0b' },
    { name: 'Proposal', value: parseInt(stats.leads.proposal) || 0, color: '#8b5cf6' },
    { name: 'Won', value: parseInt(stats.leads.won) || 0, color: '#10b981' },
    { name: 'Lost', value: parseInt(stats.leads.lost) || 0, color: '#ef4444' }
  ] : [];

  const activityData = stats?.monthlyActivity ? [
    { name: 'Calls', value: parseInt(stats.monthlyActivity.calls) || 0 },
    { name: 'Emails', value: parseInt(stats.monthlyActivity.emails) || 0 },
    { name: 'Meetings', value: parseInt(stats.monthlyActivity.meetings) || 0 }
  ] : [];

  return (
    <div className="dashboard">
      {/* Header */}
      <div className="page-header" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{
            width: 56,
            height: 56,
            borderRadius: 14,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <ChartBarIcon style={{ width: 28, height: 28, color: 'white' }} />
          </div>
          <div>
            <h1 className="page-title" style={{ fontSize: 28, fontWeight: 600, marginBottom: 4 }}>
              Sales Dashboard
            </h1>
            <p style={{ color: '#718096', fontSize: 14 }}>
              Welcome back, {user?.name}! Here's your performance overview.
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20, marginBottom: 24 }}>
        {/* Total Leads */}
        <div style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
          borderRadius: 16,
          padding: 24,
          color: 'white'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 16 }}>
            <div style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <UserGroupIcon style={{ width: 24, height: 24 }} />
            </div>
            <span style={{ fontSize: 12, opacity: 0.9 }}>All Time</span>
          </div>
          <div style={{ fontSize: 36, fontWeight: 700, marginBottom: 4 }}>
            {stats?.leads?.total || 0}
          </div>
          <div style={{ fontSize: 14, opacity: 0.9 }}>Total Leads</div>
        </div>

        {/* Pipeline Value */}
        <div style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          borderRadius: 16,
          padding: 24,
          color: 'white'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 16 }}>
            <div style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <CurrencyDollarIcon style={{ width: 24, height: 24 }} />
            </div>
            <span style={{ fontSize: 12, opacity: 0.9 }}>Active Quotes</span>
          </div>
          <div style={{ fontSize: 32, fontWeight: 700, marginBottom: 4 }}>
            {formatCurrency(stats?.pipelineValue)}
          </div>
          <div style={{ fontSize: 14, opacity: 0.9 }}>Pipeline Value</div>
        </div>

        {/* Win Rate */}
        <div style={{
          background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
          borderRadius: 16,
          padding: 24,
          color: 'white'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 16 }}>
            <div style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <TrophyIcon style={{ width: 24, height: 24 }} />
            </div>
            <span style={{ fontSize: 12, opacity: 0.9 }}>
              {stats?.winRate?.won || 0}W / {stats?.winRate?.lost || 0}L
            </span>
          </div>
          <div style={{ fontSize: 36, fontWeight: 700, marginBottom: 4 }}>
            {stats?.winRate?.rate || 0}%
          </div>
          <div style={{ fontSize: 14, opacity: 0.9 }}>Win Rate</div>
        </div>

        {/* Monthly Contacts */}
        <div style={{
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          borderRadius: 16,
          padding: 24,
          color: 'white'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 16 }}>
            <div style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <PhoneIcon style={{ width: 24, height: 24 }} />
            </div>
            <span style={{ fontSize: 12, opacity: 0.9 }}>This Month</span>
          </div>
          <div style={{ fontSize: 36, fontWeight: 700, marginBottom: 4 }}>
            {stats?.monthlyActivity?.total_contacts || 0}
          </div>
          <div style={{ fontSize: 14, opacity: 0.9 }}>Contacts Made</div>
        </div>
      </div>

      {/* Middle Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 24 }}>
        {/* Pipeline Chart */}
        <div className="card">
          <div className="card-header">
            <h3 style={{ fontSize: 16, fontWeight: 600 }}>Your Pipeline</h3>
          </div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={pipelineData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {pipelineData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Activity Breakdown */}
        <div className="card">
          <div className="card-header">
            <h3 style={{ fontSize: 16, fontWeight: 600 }}>Activity This Month</h3>
          </div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={activityData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={70}
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {activityData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginTop: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <PhoneIcon style={{ width: 16, height: 16, color: COLORS[0] }} />
                <span style={{ fontSize: 13 }}>{stats?.monthlyActivity?.calls || 0} Calls</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <EnvelopeIcon style={{ width: 16, height: 16, color: COLORS[1] }} />
                <span style={{ fontSize: 13 }}>{stats?.monthlyActivity?.emails || 0} Emails</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <UserGroupIcon style={{ width: 16, height: 16, color: COLORS[2] }} />
                <span style={{ fontSize: 13 }}>{stats?.monthlyActivity?.meetings || 0} Meetings</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Upcoming Follow-ups */}
        <div className="card">
          <div className="card-header">
            <h3 style={{ fontSize: 16, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
              <CalendarIcon style={{ width: 20, height: 20, color: '#f59e0b' }} />
              Upcoming Follow-ups
            </h3>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {stats?.followUps?.length > 0 ? (
              <div>
                {stats.followUps.map((followUp, index) => (
                  <div key={index} style={{
                    padding: '14px 20px',
                    borderBottom: index < stats.followUps.length - 1 ? '1px solid #e2e8f0' : 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <div style={{ fontWeight: 500, marginBottom: 2 }}>{followUp.lead_name}</div>
                      <div style={{ fontSize: 12, color: '#718096' }}>{followUp.lead_phone}</div>
                    </div>
                    <div style={{
                      padding: '4px 10px',
                      background: new Date(followUp.follow_up_date) <= new Date() ? '#fef3c7' : '#dbeafe',
                      color: new Date(followUp.follow_up_date) <= new Date() ? '#92400e' : '#1e40af',
                      borderRadius: 6,
                      fontSize: 12,
                      fontWeight: 500
                    }}>
                      {formatDate(followUp.follow_up_date)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ padding: 40, textAlign: 'center', color: '#718096' }}>
                <CalendarIcon style={{ width: 32, height: 32, margin: '0 auto 8px', opacity: 0.5 }} />
                <p style={{ fontSize: 14 }}>No upcoming follow-ups</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="card">
          <div className="card-header">
            <h3 style={{ fontSize: 16, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
              <ClockIcon style={{ width: 20, height: 20, color: '#3b82f6' }} />
              Recent Activity
            </h3>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {stats?.recentActivities?.length > 0 ? (
              <div>
                {stats.recentActivities.slice(0, 5).map((activity, index) => (
                  <div key={index} style={{
                    padding: '14px 20px',
                    borderBottom: index < 4 ? '1px solid #e2e8f0' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12
                  }}>
                    <div style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: activity.activity_type === 'call' ? '#dcfce7' :
                                 activity.activity_type === 'email' ? '#dbeafe' :
                                 activity.activity_type === 'meeting' ? '#fef3c7' : '#f3f4f6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {activity.activity_type === 'call' && <PhoneIcon style={{ width: 18, height: 18, color: '#16a34a' }} />}
                      {activity.activity_type === 'email' && <EnvelopeIcon style={{ width: 18, height: 18, color: '#2563eb' }} />}
                      {activity.activity_type === 'meeting' && <UserGroupIcon style={{ width: 18, height: 18, color: '#d97706' }} />}
                      {!['call', 'email', 'meeting'].includes(activity.activity_type) && 
                        <BoltIcon style={{ width: 18, height: 18, color: '#6b7280' }} />}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, fontSize: 14 }}>{activity.lead_name}</div>
                      <div style={{ fontSize: 12, color: '#718096' }}>
                        {activity.activity_type.replace('_', ' ')} • {activity.contacted_by_name || 'You'}
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: '#a0aec0' }}>
                      {formatDate(activity.created_at)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ padding: 40, textAlign: 'center', color: '#718096' }}>
                <ClockIcon style={{ width: 32, height: 32, margin: '0 auto 8px', opacity: 0.5 }} />
                <p style={{ fontSize: 14 }}>No recent activity</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Leaderboard - Only show if there are multiple users */}
      {leaderboard.length > 1 && (
        <div className="card" style={{ marginTop: 24 }}>
          <div className="card-header">
            <h3 style={{ fontSize: 16, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
              <TrophyIcon style={{ width: 20, height: 20, color: '#f59e0b' }} />
              Team Leaderboard
            </h3>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Rank</th>
                  <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Name</th>
                  <th style={{ padding: '12px 20px', textAlign: 'center', fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Won Deals</th>
                  <th style={{ padding: '12px 20px', textAlign: 'center', fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Contacts</th>
                  <th style={{ padding: '12px 20px', textAlign: 'center', fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Quotes</th>
                  <th style={{ padding: '12px 20px', textAlign: 'right', fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.map((person, index) => (
                  <tr key={person.id} style={{ 
                    borderBottom: '1px solid #e2e8f0',
                    background: person.id === user?.id ? '#eff6ff' : 'transparent'
                  }}>
                    <td style={{ padding: '14px 20px' }}>
                      {index === 0 && <span style={{ fontSize: 18 }}>🥇</span>}
                      {index === 1 && <span style={{ fontSize: 18 }}>🥈</span>}
                      {index === 2 && <span style={{ fontSize: 18 }}>🥉</span>}
                      {index > 2 && <span style={{ color: '#6b7280' }}>#{index + 1}</span>}
                    </td>
                    <td style={{ padding: '14px 20px' }}>
                      <div style={{ fontWeight: person.id === user?.id ? 600 : 500 }}>
                        {person.name} {person.id === user?.id && '(You)'}
                      </div>
                      <div style={{ fontSize: 12, color: '#718096' }}>{person.role}</div>
                    </td>
                    <td style={{ padding: '14px 20px', textAlign: 'center' }}>
                      <span style={{
                        background: '#dcfce7',
                        color: '#166534',
                        padding: '4px 10px',
                        borderRadius: 12,
                        fontSize: 13,
                        fontWeight: 600
                      }}>
                        {person.won_deals}
                      </span>
                    </td>
                    <td style={{ padding: '14px 20px', textAlign: 'center', color: '#4b5563' }}>
                      {person.contacts_this_month}
                    </td>
                    <td style={{ padding: '14px 20px', textAlign: 'center', color: '#4b5563' }}>
                      {person.quotes_this_month}
                    </td>
                    <td style={{ padding: '14px 20px', textAlign: 'right', fontWeight: 600, color: '#059669' }}>
                      {formatCurrency(person.revenue_this_month)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default SalesDashboard;
